import { AlertTriangle, CheckCircle } from 'lucide-react';
import { useLanguageStore } from '../../stores/language-store';
import { Alert, AlertDescription } from '@/core/components/ui/alert';
import { cn } from '@/core/lib/utils';

interface LanguageImpactCardProps {
  languageCode: string;
  valuesToInsert: number;
  valuesToUpdate: number;
  totalValues: number;
}

export function LanguageImpactCard({
  languageCode,
  valuesToInsert,
  valuesToUpdate,
  totalValues,
}: LanguageImpactCardProps) {
  const getLanguageByCode = useLanguageStore(state => state.getLanguageByCode);

  const language = getLanguageByCode(languageCode);
  const languageName = language?.languageName || languageCode.toUpperCase();
  const hasUpdates = valuesToUpdate > 0;

  return (
    <div
      className={cn(
        'border rounded-lg p-4',
        hasUpdates ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'
      )}
    >
      {/* Header */}
      <div className="mb-3">
        <h4 className="font-semibold text-base">
          {languageName} ({languageCode})
        </h4>
      </div>

      {/* Stats */}
      <div className="space-y-2 mb-3">
        {valuesToInsert > 0 && (
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
            <span className="text-green-700">
              <strong>{valuesToInsert}</strong> NEW translation{valuesToInsert !== 1 ? 's' : ''} to add
            </span>
          </div>
        )}

        {valuesToUpdate > 0 && (
          <div className="flex items-center gap-2 text-sm">
            <AlertTriangle className="h-4 w-4 text-yellow-600 flex-shrink-0" />
            <span className="text-yellow-700">
              <strong>{valuesToUpdate}</strong> existing translation{valuesToUpdate !== 1 ? 's' : ''} to update
            </span>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="border-t my-3" />

      {/* Message */}
      {hasUpdates ? (
        <Alert className="border-yellow-300 bg-yellow-100/50">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800 text-sm">
            <strong>{valuesToUpdate}</strong> existing {languageName} translation{valuesToUpdate !== 1 ? 's' : ''} will be <strong>UPDATED</strong>
          </AlertDescription>
        </Alert>
      ) : (
        <Alert className="border-green-300 bg-green-100/50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800 text-sm">
            No existing {languageName} translations will be overwritten
          </AlertDescription>
        </Alert>
      )}

      {/* Footer */}
      <div className="mt-3 text-xs text-muted-foreground">
        Total: {totalValues} value{totalValues !== 1 ? 's' : ''}
      </div>
    </div>
  );
}
